import { addDays, format, startOfWeek } from 'date-fns';

export const TIMESHEET_WEEK_TARGET_KEY = 'co-taskr.timesheet.weeklyTarget';
export const DEFAULT_WEEKLY_TARGET_HOURS = 40;

export type WeekDayCol = {
  key: string;
  label: string;
  sub: string;
};

export type TaskDayCell = {
  minutes: number;
  entries: any[];
};

export type TaskDayGridRow = {
  taskId: string;
  taskTitle: string;
  projectName?: string;
  cells: Record<string, TaskDayCell>;
  weekTotalMinutes: number;
};

export function getWeeklyTargetHours(): number {
  try {
    const raw = localStorage.getItem(TIMESHEET_WEEK_TARGET_KEY);
    const n = raw ? Number(raw) : DEFAULT_WEEKLY_TARGET_HOURS;
    return Number.isFinite(n) && n > 0 && n <= 80 ? n : DEFAULT_WEEKLY_TARGET_HOURS;
  } catch {
    return DEFAULT_WEEKLY_TARGET_HOURS;
  }
}

export function setWeeklyTargetHours(hours: number): void {
  localStorage.setItem(TIMESHEET_WEEK_TARGET_KEY, String(hours));
}

export function getCurrentWeekDays(weekStartsOn: 0 | 1 = 1): WeekDayCol[] {
  const weekStart = startOfWeek(new Date(), { weekStartsOn });
  return Array.from({ length: 7 }, (_, i) => {
    const day = addDays(weekStart, i);
    return {
      key: format(day, 'yyyy-MM-dd'),
      label: format(day, 'EEE'),
      sub: format(day, 'd MMM'),
    };
  });
}

export function emptyCells(dayKeys: string[]): Record<string, TaskDayCell> {
  return Object.fromEntries(dayKeys.map((k) => [k, { minutes: 0, entries: [] }]));
}

export function buildTaskDayGrid(entries: any[], days: WeekDayCol[]): TaskDayGridRow[] {
  const dayKeys = new Set(days.map((d) => d.key));
  const rowMap = new Map<string, TaskDayGridRow>();

  for (const entry of entries) {
    if (!entry.endTime || !entry.duration) continue;
    const dayKey = format(new Date(entry.startTime), 'yyyy-MM-dd');
    if (!dayKeys.has(dayKey)) continue;
    const taskId = entry.taskId || entry.task?.id;
    if (!taskId) continue;

    if (!rowMap.has(taskId)) {
      rowMap.set(taskId, {
        taskId,
        taskTitle: entry.task?.title || 'Untitled task',
        projectName: entry.task?.project?.name,
        cells: emptyCells(days.map((d) => d.key)),
        weekTotalMinutes: 0,
      });
    }

    const row = rowMap.get(taskId)!;
    row.cells[dayKey].minutes += entry.duration;
    row.cells[dayKey].entries.push(entry);
    row.weekTotalMinutes += entry.duration;
  }

  return [...rowMap.values()].sort((a, b) => b.weekTotalMinutes - a.weekTotalMinutes);
}

export function formatMinutesAsHours(minutes: number): string {
  if (minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function formatDecimalHours(minutes: number): string {
  if (minutes <= 0) return '—';
  return `${(minutes / 60).toFixed(1)}h`;
}

export function entryToHours(entry: any): number {
  if (!entry?.duration) return 0;
  return entry.duration / 60;
}
